import type { Device } from '@studio/models/device';
import type { ReactElement } from 'react';
import { useResumeAudio } from '#~/audio/sound/hooks.js';
import { useListener as useFocusListener } from '#~/control/focus/hooks.js';
import { useListener as useGamepadListener } from '#~/control/movement/gamepad/hooks.js';
import { useListener as useMovementListener } from '#~/control/movement/hooks.js';
import { useListener as useKeyboardListener } from '#~/control/movement/keyboard/hooks.js';
import { useListener as usePositionListener } from '#~/room/components/position/hooks.js';
import { useListener as useVideoListener } from '#~/room/components/video/hooks.js';
import { useSelectedVideo } from '#~/room/hooks.js';

/** @internal */
const Conditional = ({ deviceId }: { deviceId: Device['id'] }): null => {
  useFocusListener(deviceId);
  useGamepadListener(deviceId);
  useKeyboardListener(deviceId);
  useMovementListener(deviceId);
  usePositionListener(deviceId);

  return null;
};

/**
 * Command `<Listener>` component. Registers both the hot-key and gamepad observers, as well as the
 * command listeners. This is modeled as a component rather than a hook because some of the hooks
 * are enabled conditionally.
 */
export const Listener = (_: unknown): ReactElement => {
  const [selectedVideo] = useSelectedVideo();

  // Deal with audio security policy.
  useResumeAudio();

  // Activate listeners.
  useVideoListener();

  return <>{!!selectedVideo && <Conditional deviceId={selectedVideo} />}</>;
};
